import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Map, Users2, Layers } from "lucide-react";
import DOMPurify from "dompurify";
import { Navbar } from "@/components/landing/Navbar";
import { getTours } from "@/lib/api";

const TourCard = ({ t, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.4, delay: index * 0.05 }}
    data-testid={`tour-card-${t.code}`}
    className="rounded-2xl border border-white/5 bg-[#12141D] p-6 transition-colors duration-300 hover:border-[#D4AF37]/30"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {t.airline_logo ? (
            <img src={t.airline_logo} alt="" loading="lazy" className="h-6 w-6 rounded object-contain ring-1 ring-white/10" />
          ) : null}
          <span className="font-mono-data text-xs text-[#8A1538]">{t.code}</span>
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
              t.state === "Active"
                ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-400"
                : "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]"
            }`}
          >
            {t.state}
          </span>
        </div>
        <h2 className="mt-2 font-display text-xl font-bold text-[#FDFBF7]">{t.name}</h2>
        {t.desc && (
          <div
            className="mt-1 text-sm text-[#A1A1AA] [text-wrap:pretty] [&_p]:mb-2 [&_p:last-child]:mb-0 [&_a]:text-[#D4AF37] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-[#FDFBF7]"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.desc) }}
          />
        )}
      </div>
      <div className="flex-shrink-0 text-right text-xs text-[#A1A1AA]">
        <p>{t.start}</p>
        <p>to {t.end}</p>
      </div>
    </div>
    <div className="mt-4 flex items-center gap-5 text-xs text-[#A1A1AA]">
      <span className="flex items-center gap-1.5">
        <Layers className="h-3.5 w-3.5" /> {t.leg_count} legs
      </span>
      <span className="flex items-center gap-1.5">
        <Users2 className="h-3.5 w-3.5" /> {t.plt_count} pilots flown
      </span>
      <span>{t.type}</span>
    </div>
  </motion.div>
);

const Section = ({ title, items }) => (
  <div className="mt-10">
    <h2 className="font-display text-lg font-bold text-[#FDFBF7]">{title}</h2>
    <div className="mt-4 space-y-4">
      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#A1A1AA]">No tours here right now.</p>
      ) : (
        items.map((t, i) => <TourCard key={t.code} t={t} index={i} />)
      )}
    </div>
  </div>
);

export default function Tours() {
  const [data, setData] = useState({ active: [], planned: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTours()
      .then((r) => setData(r || { active: [], planned: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0B10]" data-testid="tours-page">
      <Navbar />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-28 lg:px-10 lg:pt-32">
        <div className="max-w-2xl">
          <div className="mb-4 h-0.5 w-16 bg-[#8A1538]" />
          <p className="text-overline flex items-center gap-2 text-[#8A1538]">
            <Map className="h-4 w-4" /> Multi-Leg Campaigns
          </p>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-[#FDFBF7] lg:text-5xl [text-wrap:balance]">
            Tours
          </h1>
          <p className="mt-3 text-sm text-[#A1A1AA] [text-wrap:pretty]">
            Fly a sequence of legs across a themed route. Log in on the crew center to start one.
          </p>
        </div>

        {loading ? (
          <p className="mt-16 text-center text-sm text-[#A1A1AA]">Loading tours…</p>
        ) : (
          <>
            <Section title="Active" items={data.active} />
            <Section title="Planned" items={data.planned} />
          </>
        )}
      </main>
    </div>
  );
}
